import { CacheService, CacheConfig, CacheStore, CacheSchema, CacheItem } from "@microsoft/mgt-react";
import { schemas } from '@microsoft/mgt-components/dist/es6/graph/cacheStores';
import { graphService } from '../services/graph.service';

// Team and Entra group pictures get a groups store inside mgt's own photos
// database. Adding a store needs a version bump, and mgt opens that database from
// its own copy of the schema - so extend that copy in place rather than declaring
// a parallel one. Both sides then agree on version 2; declaring our own would
// leave mgt opening v1 and silently losing its photo cache to VersionError.
//
// CacheStore reads schema.version and schema.stores at open time, so this only
// has to run before the first cache read or write, not before getCache. mgt's
// upgrade handler creates any store in the schema the database does not have,
// leaving users and contacts intact. #34542
export const GROUP_PHOTO_STORE = 'groups';
(schemas.photos.stores as Record<string, string>)[GROUP_PHOTO_STORE] = GROUP_PHOTO_STORE;
schemas.photos.version = 2;

const cacheSchema = schemas.photos as CacheSchema;
  
interface CachePhoto extends CacheItem {
    photo?: string;
}

// retrieves invalidation time from cache config
const getPhotoInvalidationTime = (): number =>
CacheService.config.photos.invalidationPeriod || CacheService.config.defaultInvalidationPeriod;

// checks for if cache is enabled
const photosCacheEnabled = (): boolean => CacheService.config.photos.isEnabled && CacheService.config.isEnabled;

// declare the desired cache store
let cache: CacheStore<CachePhoto>
let groupCache: CacheStore<CachePhoto>
if (photosCacheEnabled()) {
    cache = CacheService.getCache<CachePhoto>(cacheSchema, 'users');
    groupCache = CacheService.getCache<CachePhoto>(cacheSchema, GROUP_PHOTO_STORE);
}

const graphAPI = new graphService();

// Remembers a miss as well as a hit, same reasoning as getNewGroupPhoto
// below: a user with no picture 404s from Graph, and without catching that,
// the miss was never cached - so the avatar re-requested (and re-threw
// uncaught) on every render/remount instead of caching the "no photo"
// result once. #34542
const pendingUserPhoto: { [id: string]: Promise<string> } = {};

const getNewPhoto = (idOrUpn: string): Promise<string> => {
    if (!photosCacheEnabled()) return Promise.resolve(undefined);

    pendingUserPhoto[idOrUpn] ??= graphAPI
        .getUserPhoto(idOrUpn)
        .catch(() => "")
        .then(src => {
            cache.putValue(idOrUpn, { photo: src ?? "" });
            delete pendingUserPhoto[idOrUpn];
            return src ?? "";
        });

    return pendingUserPhoto[idOrUpn];
}

export const setCachedPhoto = (idOrUpn: string, data: string) => {
    if (photosCacheEnabled())
        cache.putValue(idOrUpn, { photo: data });
}

export const getCachedPhoto = async(idOrUpn: string) => {
    // check if the cache is enabled
    if (photosCacheEnabled()) {
        const photoObj = await cache.getValue(idOrUpn);
        // check if an item is retrieved, and if it's not expired
        if (photoObj && getPhotoInvalidationTime() > Date.now() - photoObj.timeCached)
            return photoObj.photo;
        else
            return getNewPhoto(idOrUpn);
    }
}

// Team and Entra group photos, in their own store alongside the user ones.
// Remembers a miss as well as a hit: a group with no picture would otherwise be
// re-requested on every render of an Available To column. #34542
const pendingGroupPhoto: { [id: string]: Promise<string> } = {};

const getNewGroupPhoto = (id: string): Promise<string> => {
    pendingGroupPhoto[id] ??= graphAPI
        .getGroupPhoto(id)
        .catch(() => "")
        .then(photo => {
            if (photosCacheEnabled()) groupCache.putValue(id, { photo: photo ?? "" });
            delete pendingGroupPhoto[id];
            return photo ?? "";
        });

    return pendingGroupPhoto[id];
}

export const getCachedGroupPhoto = async (id: string): Promise<string> => {
    if (!id) return "";

    if (!photosCacheEnabled()) return getNewGroupPhoto(id);

    const photoObj = await groupCache.getValue(id);
    if (photoObj && getPhotoInvalidationTime() > Date.now() - photoObj.timeCached)
        return photoObj.photo ?? "";

    return getNewGroupPhoto(id);
}
